import { EarningType, PayrollBreakdownStatus } from "../../../__generated__/graphql";
import { MissionPayrollBreakdownFieldsFragment } from "../../../__generated__/operations/graphql";
import { toFixedIfNecessary } from "../../../utils";
import { FormData } from "./PayrollBreakdownEditor";

const displayDefaultPlusMinusValue = (deltaWork?: number | null): "plus" | "minus" =>
    (deltaWork ?? 0) < 0 ? "minus" : "plus";

const parseTotalWorkedDays = (totalWorkedDays?: string | null): number | undefined => {
    if (totalWorkedDays != null) {
        const parsedValue = parseFloat(totalWorkedDays);
        if (!isNaN(parsedValue)) {
            return parsedValue;
        }
        return undefined;
    }
    return undefined;
};

// A monthly mission starting after the last tracked week of its first month gets a payroll
// breakdown whose start date is after its end date: no timesheet can ever be attached to it.
export const isEmptyPayrollBreakdownPeriod = (
    payrollBreakdown: Pick<MissionPayrollBreakdownFieldsFragment, "startDate" | "endDate">,
): boolean =>
    !!(
        payrollBreakdown.startDate &&
        payrollBreakdown.endDate &&
        payrollBreakdown.startDate > payrollBreakdown.endDate
    );

// There is nothing to track for the empty period of a monthly mission, whatever its payroll source:
// the backend auto submits it at the end of the period (it still carries the prorata salary of the
// first month), so there is nothing to do on it while it is open.
export const isPendingAutoSubmitPayrollBreakdown = (
    payrollBreakdown: Pick<
        MissionPayrollBreakdownFieldsFragment,
        "status" | "startDate" | "endDate" | "missionInfo"
    >,
): boolean =>
    payrollBreakdown.status === PayrollBreakdownStatus.Open &&
    payrollBreakdown.missionInfo.earningType === EarningType.Monthly &&
    isEmptyPayrollBreakdownPeriod(payrollBreakdown);

export const buildPayrollBreakdownFormFromModel = (
    payrollBreakdown: MissionPayrollBreakdownFieldsFragment,
    hoursPerDay: number,
): FormData => {
    return {
        usedVacationDays:
            payrollBreakdown.usedVacationMinutes != null
                ? toFixedIfNecessary(payrollBreakdown.usedVacationMinutes / 60 / hoursPerDay, 2)
                : undefined,
        // deltaWork is a signed number of minutes; the form splits it into an unsigned amount of
        // hours plus a mode, and getDeltaWork() puts the sign back when saving.
        deltaWorkHours:
            payrollBreakdown.deltaWork != null
                ? Math.abs(payrollBreakdown.deltaWork) / 60
                : undefined,
        totalWorkedDays: parseTotalWorkedDays(payrollBreakdown.totalWorkedDays),
        deltaWorkMode: displayDefaultPlusMinusValue(payrollBreakdown.deltaWork),
        absences: payrollBreakdown.absences || [],
        bonuses: payrollBreakdown.bonuses || [],
        auxiliarySalaries: payrollBreakdown.auxiliarySalaries || [],
        effectiveFees: payrollBreakdown.effectiveFees || [],
        dynamicFees: payrollBreakdown.dynamicFees || [],
        overtimePaid100Hours: (payrollBreakdown.overtimePaid100 || 0) / 60,
        overtimePaid125Hours: (payrollBreakdown.overtimePaid125 || 0) / 60,
        totalWork100Hours:
            payrollBreakdown.totalWork100 != null ? payrollBreakdown.totalWork100 / 60 : undefined,
        totalWork115Hours: (payrollBreakdown.totalWork115 || 0) / 60,
        totalWork125Hours: (payrollBreakdown.totalWork125 || 0) / 60,
        totalWork150Hours: (payrollBreakdown.totalWork150 || 0) / 60,
        totalWork200Hours: (payrollBreakdown.totalWork200 || 0) / 60,
    };
};
